<template>
  <el-drawer
    title=""
    :visible.sync="showVisibleDocumentSend"
    :with-header="false"
    custom-class="wrapper__document-send-drawer"
    @closed="onCloseDrawer"
    @open="fixDrawerZIndex"
    @opened="fixDrawerZIndex"
    size="100%"
    append-to-body
  >
    <section class="p-4">
      <button
        class="btn-close-drawer"
        @click="onCloseDrawer"
        type="button"
        aria-label="Cerrar"
      >
        <i class="el-icon-close"></i>
      </button>

      <h3
        class="h3 text-center font-semi-bold text-break text-color-primary mb-3"
      >
        Envía documento con <br />
        mensaje adjunto
      </h3>

      <!-- Canal toggle -->
      <div class="wsd-channel mb-4">
        <button
          :class="['wsd-ch-btn', { active: channel === 'email' }]"
          @click="channel = 'email'"
        >
          <i class="el-icon-message"></i> Email
        </button>
        <button
          :class="['wsd-ch-btn', { active: channel === 'whatsapp' }]"
          @click="channel = 'whatsapp'"
        >
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
            width="14"
            height="14"
            style="display:inline;vertical-align:middle;margin-right:4px;"
            alt=""
          />
          WhatsApp
        </button>
      </div>

      <!-- ══ EMAIL ══ -->
      <div v-if="channel === 'email'" :key="formKey">
        <div class="d-flex align-items-center">
          <p class="mb-0">Para:</p>
          <TagInput @onChangeTags="onTagToEmails" :values="to" />
        </div>
        <div class="d-flex align-items-center mb-4">
          <p class="mb-0">CC:</p>
          <TagInput @onChangeTags="onTagCCEmails" />
        </div>
        <EditorWysiwyg
          @onEditorChange="onEditorChange"
          :setText.sync="message"
        />
        <UploadFile @uploadFile="uploadFile" :initialFiles="attachFiles" />
      </div>

      <!-- ══ WHATSAPP ══ -->
      <div v-else-if="channel === 'whatsapp'" class="wsd-wa-form">
        <!-- WhatsApp no configurado -->
        <div v-if="!whatsappReady" class="wsd-wa-empty">
          <div class="wsd-wa-empty-icon">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
              width="32"
              height="32"
              alt=""
            />
          </div>
          <p class="wsd-wa-empty-title">WhatsApp no está configurado</p>
          <p class="wsd-wa-empty-sub">
            Conecta tu línea en Configuración → Integraciones para enviar
            mensajes.
          </p>
          <el-button
            type="primary"
            size="small"
            @click="$router.push('/dashboard/configurations')"
          >
            <i class="el-icon-setting"></i> Configurar WhatsApp
          </el-button>
        </div>

        <template v-else>
          <!-- Teléfonos (multi-contacto) -->
          <div class="wsd-wa-field mb-3">
            <label class="wsd-label">Números de WhatsApp</label>

            <!-- Aviso cuando el cliente no tiene número -->
            <div v-if="!toPhone && !wa.phones.length" class="wsd-wa-no-phone">
              <i class="el-icon-warning-outline"></i>
              <span>
                El cliente no tiene un número de WhatsApp guardado. Agrégalo
                abajo.
              </span>
            </div>

            <div class="wsd-phones">
              <div
                v-for="(p, idx) in wa.phones"
                :key="p + '-' + idx"
                class="wsd-phone-chip"
              >
                <i class="el-icon-mobile-phone"></i>
                <span>{{ p }}</span>
                <button
                  type="button"
                  class="wsd-phone-remove"
                  @click="removePhone(idx)"
                  aria-label="Eliminar"
                >
                  <i class="el-icon-close"></i>
                </button>
              </div>

              <!-- Autocompletado: busca contactos por nombre / teléfono -->
              <div class="wsd-phone-input-row">
                <el-autocomplete
                  v-model="newPhoneInput"
                  :fetch-suggestions="searchContacts"
                  placeholder="Busca un contacto o escribe el número (+593...)"
                  size="small"
                  style="flex:1;"
                  :trigger-on-focus="false"
                  @select="onContactSelect"
                  @keydown.native.enter.prevent="addPhone"
                >
                  <template slot-scope="{ item }">
                    <div class="wsd-ac-item">
                      <span class="wsd-ac-item__name">{{ item.name }}</span>
                      <small class="wsd-ac-item__phone">{{ item.phone }}</small>
                    </div>
                  </template>
                </el-autocomplete>
                <button
                  type="button"
                  class="wsd-phone-add-btn"
                  :disabled="!newPhoneInput.trim()"
                  @click="addPhone"
                  title="Agregar contacto"
                >
                  <i class="el-icon-plus"></i>
                </button>
              </div>
            </div>
            <div class="wsd-hint">
              Busca contactos guardados o escribe el número en formato
              internacional (+código_país + número). Puedes agregar varios.
            </div>
          </div>

          <!-- Selector de plantilla -->
          <div class="wsd-wa-field mb-3">
            <WhatsAppTemplatePicker
              :value="wa.templateData"
              :module-type="moduleType"
              @input="onWaTemplateChange"
            />
          </div>
        </template>
      </div>

      <!-- Botón enviar -->
      <button
        class="btn btn-lg md__btn-primary btn-send-document"
        :class="{ 'btn-send-disabled': !canSend }"
        :disabled="sending || !canSend"
        @click="handleSend"
      >
        <i v-if="!sending" class="el-icon-s-promotion"></i>
        <i v-else class="el-icon-loading"></i>
      </button>
    </section>
  </el-drawer>
</template>

<script>
import EditorWysiwyg from "./EditorWysiwyg";
import UploadFile from "./UploadFile";
import TagInput from "./TagInput";
import WhatsAppTemplatePicker from "./WhatsAppTemplatePicker";

import { icoSend, icoPlane } from "@/services/resources";
import configService from "../configurations/services/configService";
import businessService from "@/modules/dashboard/business/services/businessService";

export default {
  name: "WrapperSendDocumentDrawer",

  components: { TagInput, EditorWysiwyg, UploadFile, WhatsAppTemplatePicker },

  props: {
    visibleDocumentSend: { default: false },
    toMails: { type: Array, default: () => [] },
    toPhone: { type: String, default: "" },
    moduleType: { type: String, default: "" },
    messageableType: { type: String, default: "" },
    messageableId: { type: [Number, String], default: null },
    clientId: { type: [Number, String], default: null },
    recipientName: { type: String, default: "" },
    isLoadingConfigTexts: { default: true, type: Boolean },
    defaultMessage: { type: String, default: "" },
    draftKey: { type: String, default: "" }
  },

  data() {
    return {
      icoSend,
      icoPlane,
      channel: "email",
      // Email
      message: "",
      initialMessage: "",
      to: this.toMails,
      cc: [],
      attachFiles: [],
      formKey: 0,
      // WhatsApp
      whatsappReady: false,
      newPhoneInput: "",
      wa: {
        phones: this.toPhone ? [this.toPhone] : [],
        templateData: null // { templateId, templateName, variables, template }
      },
      sending: false,
      showVisibleDocumentSend: this.visibleDocumentSend
    };
  },

  computed: {
    canSend() {
      if (this.channel === "email") {
        return this.to.length > 0;
      }
      // WhatsApp: necesita al menos 1 teléfono + plantilla
      return !!(this.wa.phones.length > 0 && this.wa.templateData?.templateId);
    }
  },

  watch: {
    visibleDocumentSend(value) {
      this.showVisibleDocumentSend = value;
      if (value) {
        this.initForm();
        // Re-verificar el estado de WhatsApp cada vez que se abre el drawer.
        // Soluciona el caso de que la config se conectó DESPUÉS de montar el padre.
        this.checkWhatsAppStatus();
      }
    },
    channel(val) {
      // Si el usuario salta a WhatsApp, asegurarnos de tener el estado fresco
      if (val === "whatsapp") {
        this.checkWhatsAppStatus();
      }
    },
    toMails(mails) {
      this.to = mails;
    },
    toPhone(val) {
      this.wa.phones = val ? [val] : [];
      this.newPhoneInput = "";
    }
  },

  mounted() {
    if (this.visibleDocumentSend) {
      this.initForm();
    }
    this.checkWhatsAppStatus();
  },

  methods: {
    async checkWhatsAppStatus() {
      // DEV: forzar UI completa sin necesidad de configurar WhatsApp
      // Activar en consola: localStorage.setItem('wa_dev_mode', '1')
      if (localStorage.getItem("wa_dev_mode") === "1") {
        this.whatsappReady = true;
        return;
      }
      try {
        const res = await configService.getWhatsAppConfig();
        const cfg = res.data?.data;
        this.whatsappReady = !!(
          cfg &&
          cfg.status === "active" &&
          cfg.is_active
        );
      } catch {
        this.whatsappReady = false;
      }
    },

    fixDrawerZIndex() {
      const applyZIndex = () => {
        const drawer = document.querySelector(".wrapper__document-send-drawer");
        const wrapper = drawer ? drawer.closest(".el-drawer__wrapper") : null;
        if (wrapper) {
          wrapper.style.zIndex = "2300";
          wrapper.classList.add("wrapper__document-send-drawer-parent");
        }
      };
      this.$nextTick(() => {
        applyZIndex();
        setTimeout(applyZIndex, 50);
      });
    },

    async initForm() {
      this.cc = [];
      this.wa.phones = this.toPhone ? [this.toPhone] : [];
      this.newPhoneInput = "";
      this.wa.templateData = null;

      if (this.isLoadingConfigTexts) {
        try {
          const response = await configService.getUserConfigTexts();
          this.message = response.data.data
            ? response.data.data.message
            : this.defaultMessage || "";
        } catch {
          this.message = this.defaultMessage || "";
        }
      } else {
        this.message = this.defaultMessage || "";
      }

      this.initialMessage = this.message;

      if (this.draftKey) {
        const saved = localStorage.getItem(this.draftKey);
        if (saved !== null) this.message = saved;
      }
      this.formKey++;
    },

    // Quill emite text-change también al montar con el contenido inicial, así que
    // un borrador igual al mensaje por defecto no aporta nada: se elimina en vez de
    // guardarse. Sin esto, cada documento cuyo drawer se abrió dejaba una copia del
    // mensaje por defecto (con imágenes en base64 pesa cientos de KB) y la cuota de
    // 5 MB de localStorage se agotaba: setItem lanzaba QuotaExceededError y ningún
    // borrador nuevo se guardaba.
    saveDraft(text) {
      if (!this.draftKey) return;
      if (!text || text === this.initialMessage) {
        localStorage.removeItem(this.draftKey);
        return;
      }
      try {
        localStorage.setItem(this.draftKey, text);
      } catch (e) {
        this.purgeOtherDrafts();
        try {
          localStorage.setItem(this.draftKey, text);
        } catch (e2) {
          // Sin espacio incluso tras purgar: el mensaje sigue en memoria (this.message).
        }
      }
    },

    purgeOtherDrafts() {
      Object.keys(localStorage)
        .filter(k => k.startsWith("draft-") && k !== this.draftKey)
        .forEach(k => localStorage.removeItem(k));
    },

    onCloseDrawer() {
      this.$emit("onCloseDrawer");
    },

    onEditorChange({ text }) {
      this.message = text;
      this.saveDraft(text);
    },

    onTagToEmails(emails) {
      this.to = emails;
    },
    onTagCCEmails(emails) {
      this.cc = emails;
    },
    uploadFile({ files }) {
      this.attachFiles = files;
    },

    onWaTemplateChange(data) {
      this.wa.templateData = data;
    },

    normalizePhone(raw) {
      if (!raw) return "";
      // Conservar solo + y dígitos
      let p = String(raw)
        .trim()
        .replace(/[^\d+]/g, "");
      if (!p) return "";
      // Si no comienza con + pero tiene dígitos, prefijar +
      if (!p.startsWith("+")) p = "+" + p.replace(/^\++/, "");
      return p;
    },

    isValidPhone(p) {
      return /^\+[1-9]\d{7,14}$/.test(p);
    },

    addPhone() {
      const raw = this.newPhoneInput;
      if (!raw || !raw.trim()) return;
      const phone = this.normalizePhone(raw);
      if (!this.isValidPhone(phone)) {
        this.$message.warning(
          "Número inválido. Usa formato internacional: +593999123456"
        );
        return;
      }
      if (this.wa.phones.includes(phone)) {
        this.newPhoneInput = "";
        return;
      }
      this.wa.phones.push(phone);
      this.newPhoneInput = "";
    },

    removePhone(idx) {
      this.wa.phones.splice(idx, 1);
    },

    async searchContacts(query, cb) {
      // Si lo que está escrito ya parece un teléfono, no buscar contactos
      const trimmed = (query || "").trim();
      if (!trimmed || /^[+\d]/.test(trimmed)) {
        cb([]);
        return;
      }
      try {
        const res = await businessService.getBusiness({
          params: { name: trimmed }
        });
        const raw = res.data?.clients || [];
        const results = raw
          .map(c => ({
            id: c.id,
            name: c.name || c.business_name || "",
            phone: this.normalizePhone(c.mobile || c.phone || ""),
            value: c.name || c.business_name || ""
          }))
          .filter(c => c.phone && this.isValidPhone(c.phone));
        cb(results);
      } catch {
        cb([]);
      }
    },

    onContactSelect(item) {
      if (!item || !item.phone) {
        this.newPhoneInput = "";
        return;
      }
      if (!this.wa.phones.includes(item.phone)) {
        this.wa.phones.push(item.phone);
      }
      this.newPhoneInput = "";
    },

    async handleSend() {
      if (!this.canSend) return;
      if (this.channel === "email") {
        await this.onDocumentSend();
      } else {
        await this.onWhatsAppSend();
      }
    },

    async onDocumentSend() {
      let data = new FormData();
      data.append("message", this.message);
      data.append("cc", JSON.stringify(this.cc));
      data.append("to", JSON.stringify(this.to));
      this.attachFiles.forEach(file => {
        if (file.raw) data.append("files[]", file.raw);
      });
      if (this.draftKey) localStorage.removeItem(this.draftKey);
      this.$emit("onDocumentSend", { data });
    },

    async onWhatsAppSend() {
      // Si quedó algo escrito en el input de teléfono, intentar agregarlo
      if (this.newPhoneInput && this.newPhoneInput.trim()) {
        this.addPhone();
      }

      const phones = this.wa.phones.filter(p => this.isValidPhone(p));
      if (!phones.length) {
        this.$message.warning(
          "Agrega al menos un número en formato internacional: +593999123456"
        );
        return;
      }

      // ── DEV MODE: simular envío exitoso sin tocar el backend ──
      if (localStorage.getItem("wa_dev_mode") === "1") {
        this.sending = true;
        await new Promise(r => setTimeout(r, 600));
        this.sending = false;
        this.$notifications.success({
          message:
            "✓ [Modo prueba] Mensaje simulado enviado a " +
            phones.length +
            ' contacto(s) con la plantilla "' +
            (this.wa.templateData.templateName || "") +
            '".'
        });
        this.$emit("onWhatsAppSend", { simulated: true, phones });
        this.onCloseDrawer();
        return;
      }

      this.sending = true;
      let okCount = 0;
      let failCount = 0;
      let lastError = "";

      try {
        for (const phone of phones) {
          const payload = {
            template_id: this.wa.templateData.templateId,
            recipient_phone: phone,
            variables: this.wa.templateData.variables || []
          };
          if (this.recipientName) payload.recipient_name = this.recipientName;
          if (this.clientId) payload.client_id = this.clientId;
          if (this.messageableType && this.messageableId) {
            payload.messageable_type = this.messageableType;
            payload.messageable_id = this.messageableId;
          }

          // skipErrorNotification: el catch maneja el toast contextual,
          // evita el doble toast del interceptor global en 500
          try {
            const res = await configService.sendWhatsAppMessage(payload, {
              skipErrorNotification: true
            });
            if (res.data?.success) {
              okCount++;
              this.$emit("onWhatsAppSend", res.data.data);
            } else {
              failCount++;
              lastError = res.data?.message || "No se pudo enviar el mensaje.";
            }
          } catch (err) {
            failCount++;
            lastError =
              err.response?.data?.message ||
              err.response?.data?.error ||
              err.message ||
              "Error inesperado al enviar.";
          }
        }

        // Resumen final
        if (okCount > 0 && failCount === 0) {
          this.$notifications.success({
            message:
              okCount === 1
                ? "Mensaje enviado por WhatsApp."
                : `${okCount} mensajes enviados por WhatsApp.`
          });
          this.onCloseDrawer();
        } else if (okCount > 0 && failCount > 0) {
          this.$notifications.warning({
            message: `${okCount} enviados, ${failCount} fallaron. ${lastError}`
          });
        } else {
          this.$notifications.error({
            message: lastError || "No se pudo enviar el mensaje."
          });
        }
      } finally {
        this.sending = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* ── Channel toggle ── */
.wsd-channel {
  display: flex;
  gap: 8px;
  justify-content: center;
}
.wsd-ch-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 18px;
  border: 1px solid #e4eaf6;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: #606266;
  transition: all 0.15s;
  &:hover {
    border-color: #31355c;
    color: #31355c;
  }
  &.active {
    border-color: #31355c;
    background: #31355c;
    color: #fff;
  }
}

/* ── WhatsApp form ── */
.wsd-wa-form {
  display: flex;
  flex-direction: column;
}
.wsd-wa-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
}
.wsd-wa-empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f0fdf4;
  border: 2px solid #d1fae5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}
.wsd-wa-empty-title {
  font-size: 14px;
  font-weight: 700;
  color: #31355c;
  margin: 0 0 6px;
}
.wsd-wa-empty-sub {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 16px;
  line-height: 1.5;
}
.wsd-wa-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.wsd-label {
  font-size: 11px;
  font-weight: 700;
  color: #31355c;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.wsd-hint {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

/* ── Aviso cliente sin teléfono ── */
.wsd-wa-no-phone {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #9a3412;
  line-height: 1.4;
  i {
    color: #f97316;
    font-size: 14px;
    margin-top: 1px;
  }
}

/* ── Multi-teléfono ── */
.wsd-phones {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.wsd-phone-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px 5px 10px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  font-size: 12px;
  color: #065f46;
  width: fit-content;
  i.el-icon-mobile-phone {
    color: #10b981;
    font-size: 14px;
  }
}
.wsd-phone-remove {
  background: transparent;
  border: none;
  color: #065f46;
  cursor: pointer;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  i {
    font-size: 11px;
  }
  &:hover {
    background: #d1fae5;
    color: #064e3b;
  }
}
.wsd-phone-input-row {
  display: flex;
  gap: 6px;
  align-items: stretch;
  ::v-deep .el-input,
  ::v-deep .el-autocomplete {
    flex: 1;
  }
}
.wsd-ac-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 1px 0;
}
.wsd-ac-item__name {
  font-size: 13px;
  font-weight: 600;
  color: #31355c;
  flex-shrink: 0;
}
.wsd-ac-item__phone {
  font-size: 11px;
  color: #909399;
  font-family: monospace;
  margin-left: auto;
}
.wsd-phone-add-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #e4eaf6;
  background: #fff;
  color: #31355c;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
  i {
    font-size: 14px;
  }
  &:hover:not(:disabled) {
    background: #31355c;
    color: #fff;
    border-color: #31355c;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

/* ── Existing ── */
.btn-close-drawer {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #f0f2f7;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
  i {
    font-size: 18px;
    color: #31355c;
    font-weight: 700;
  }
  &:hover {
    background: #e4e8f0;
  }
}
.btn-send-document {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin: 28px auto 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  i {
    font-size: 26px;
    color: #fff;
  }
  &.btn-send-disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}
</style>

<style lang="scss">
.wrapper__document-send-drawer-parent {
  z-index: 2300 !important;
}
</style>
